import { z } from "zod";
import { AuditActionSchema } from "./records.js";

/**
 * Platform view (requirements.md section 12): who is using the portal,
 * how much, and where it is refusing them. Internal team only, and
 * cross-tenant by definition - a different question from the Focus page,
 * which is about what is wrong inside a customer's AWS estate.
 */

/**
 * Gairm staff belong to no customer organisation, so they partition
 * under a sentinel. Server-side only - it is never accepted from a
 * request, and no customer organisation can be called this.
 */
export const INTERNAL_ORGANISATION = "__internal__";
/** Platform-wide totals live in their own partition, alongside the per-tenant ones. */
export const ALL_SCOPE = "__all__";

/**
 * `PortalUsers` - PK `organisation_id`, SK `user_id`.
 *
 * Identity stays in Cognito; this is the activity Cognito cannot answer.
 * `last_seen_at` is a session, `last_action_at` is a change - section
 * 12.1 turns on those being different, because a user who reads
 * dashboards daily and changes nothing writes no audit records.
 */
export const PortalUserRecordSchema = z.object({
  organisation_id: z.string().min(1),
  user_id: z.string().min(1),
  email: z.string().optional(),
  role: z.enum(["internal-team", "customer"]),
  first_seen_at: z.string().datetime({ offset: true }),
  /** A session was established. Never an audit record: signing in is not a privileged action. */
  last_seen_at: z.string().datetime({ offset: true }).optional(),
  /** An audit record was written. */
  last_action_at: z.string().datetime({ offset: true }).optional(),
  last_action: AuditActionSchema.optional(),
  /** ADR-0017 section 4: a staff member's role; absent reads as `gairm-staff`. Customer rows never carry one. */
  staff_role: z.enum(["gairm-staff", "gairm-reader", "gairm-superadmin"]).optional(),
});
export type PortalUserRecord = z.infer<typeof PortalUserRecordSchema>;

/**
 * `AuditDailyCounts` - PK `${scope}#${metric}`, SK `YYYY-MM-DD`.
 *
 * The same shape as `EventDailyCounts` and for the same reason: drawing a
 * 90-day chart must cost the window, not the whole 12-month audit log.
 * Written by a stream off `AuditLog`, exactly-once. Per-action counts sit
 * on the day item as `by:<action>` attributes, so one query returns the
 * total and its breakdown together.
 */
export const AuditMetricSchema = z.enum(["changes", "denied", "rejected", "break_glass"]);
export type AuditMetric = z.infer<typeof AuditMetricSchema>;

export const AuditDailyCountRecordSchema = z.object({
  /** `${organisation_id | user id | __all__}#${metric}` */
  pk: z.string().min(1),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().nonnegative(),
});
export type AuditDailyCountRecord = z.infer<typeof AuditDailyCountRecordSchema>;

export const BY_ACTION_PREFIX = "by:";

export interface DailyPoint {
  date: string;
  count: number;
}

export interface PlatformSummaryResponse {
  window: 7 | 30 | 60 | 90;
  generatedAt: string;
  /** Day buckets across the window, zero-filled so a quiet day is visible as a gap. */
  changesByDay: DailyPoint[];
  changesByMonth: DailyPoint[];
  totals: {
    changes: number;
    denied: number;
    rejected: number;
    breakGlass: number;
  };
  /** Which kinds of change, because twelve promotions and twelve overrides are not the same week. */
  changesByAction: Array<{ action: string; count: number }>;
  /** The owner's ask of 2026-09-21: the customer side of the platform, beside the user side. */
  customers: {
    organisations: number;
    environments: number;
    live: number;
    /** Heartbeat within the last three hours. */
    reporting: number;
    noHeartbeat: number;
    /** Distinct Connect instances the stacks report. */
    connectInstances: number;
    onCurrentTemplate: number;
    behindTemplate: number;
    /** Live environments whose meter reported this month. */
    usageReported: number;
  };
  users: {
    total: number;
    internal: number;
    customer: number;
    /** Established a session in the window. */
    signedIn: number;
    /** Wrote an audit record in the window. These are different figures and are never merged. */
    madeAChange: number;
    neverSignedIn: number;
    stale: number;
  };
  /** Stated rather than assumed: the number behind "stale". */
  staleAfterDays: number;
  approvalsAgeingOverDays: number;
  approvalsAgeing: Array<{
    promotionId: string;
    organisationId: string;
    environmentId: string;
    profileId: string;
    requestedBy: string;
    requestedAt: string;
    ageDays: number;
  }>;
}

export interface PlatformTenantRow {
  organisationId: string;
  name: string;
  status: string;
  environments: number;
  users: number;
  changes: number;
  denied: number;
  lastChangeAt?: string;
  lastSignInAt?: string;
  /** Never active and gone quiet are different conditions and are reported apart. */
  activity: "never_active" | "quiet" | "active";
}

export interface PlatformTenantsResponse {
  window: 7 | 30 | 60 | 90;
  quietAfterDays: number;
  tenants: PlatformTenantRow[];
}

export interface PlatformUserRow {
  userId: string;
  email?: string;
  organisationId: string;
  organisationName: string;
  role: "internal-team" | "customer";
  firstSeenAt: string;
  lastSeenAt?: string;
  lastActionAt?: string;
  lastAction?: string;
  changes: number;
  staleDays?: number;
  /** What a dormant account can still do - the risk is retained access, not dormancy. */
  access: Array<{ environmentId: string; role: "admin" | "manager" }>;
}

export interface PlatformUsersResponse {
  window: 7 | 30 | 60 | 90;
  staleAfterDays: number;
  users: PlatformUserRow[];
}

/**
 * Feature adoption (the owner's ask of 2026-09-23, #323): the audit
 * actions that change what a customer's flows do. The adoption table's
 * "changes" figure is these, from the `by:<action>` day counters - not
 * the organisation's whole `changes` count, which also holds preference
 * saves, invitations and a promotion being asked for.
 */
export const CONFIGURATION_CHANGE_ACTIONS = [
  "profile.created",
  "profile.published",
  "rollback.applied",
  "promotion.applied",
  "override.set",
  "override.cleared",
  "binding.created",
  "binding.removed",
  "reference_alias.set",
  "reference_alias.removed",
] as const satisfies ReadonlyArray<z.infer<typeof AuditActionSchema>>;

export interface PlatformAdoptionFigures {
  /** Configuration changes applied in the window (`CONFIGURATION_CHANGE_ACTIONS`). */
  changes: number;
  /** Promotions applied in the window - configuration carried up the chain, e.g. dev to prod. */
  promotions: number;
  /**
   * Changes that went to a manager (ADR-0008): promotions, gated direct
   * edits and gated rollbacks alike. `needed`, `approved` and `rejected`
   * are the requests made in the window and what became of them;
   * `waiting` is every request pending now, however old.
   */
  approvals: { needed: number; approved: number; rejected: number; waiting: number };
}

export interface PlatformAdoptionRow extends PlatformAdoptionFigures {
  organisationId: string;
  name: string;
  status: string;
}

export interface PlatformAdoptionResponse {
  window: 7 | 30 | 60 | 90;
  /** Every organisation, including those with nothing yet - that is the adoption signal. */
  organisations: PlatformAdoptionRow[];
  /** Platform-wide: the sum of the rows. */
  totals: PlatformAdoptionFigures;
}
